import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Lightbox from 'lightbox-react';
import { usePubNub } from 'pubnub-react';
import Box from '@mui/material/Box';

import ListingDetailDrawer from 'app/components/ListingDetailDrawer';

import {
  useLazyGetListingQuery,
  useGetAppConfigQuery,
  LISTING_TAG,
} from 'app/api/mainApi';

import ModalsContext from 'app/contexts/ModalsContext';
import ReportContentModal from 'app/components/modals/ReportContentModal';
import { callButton } from 'app/helpers/listingHelpers';
import useInvalidateCacheTagsDispatcher from 'app/hooks/useInvalidateCacheTagsDispatcher';
import usePubNubSubscriptions from 'app/hooks/usePubNubSubscriptions';

const ListingDetailDrawerContainer = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // parseInt works wonders here for our use case
  // it will only parse the first number in the string
  // and ignore the rest, so even if there's a number in the
  // listing slug that isn't the listing_id, it will be disregarded
  const listingId = parseInt(searchParams.get('listing_id'));
  const { openModal } = useContext(ModalsContext);
  const [lightboxIsOpen, setLightboxIsOpen] = useState(false);
  const pubNubClient = usePubNub();
  const { dispatchInvalidateCacheTagsEvent } = useInvalidateCacheTagsDispatcher();
  const [prevDocTitle, setPrevDocTitle] = useState(null);

  // try using an additional (and nullable) variable so
  // we can eliminate the flash of old listing content
  // when we we reopen the drawer. localListing is the data
  // that is passed to the component. open/close drawer
  // behavior is determined by the presence of localListing
  const [localListing, setLocalListing] = useState(null);
  const [triggerGetListingQuery, listingQueryResult] = useLazyGetListingQuery();
  const { data: listing } = listingQueryResult;
  const { data: appConfig } = useGetAppConfigQuery();
  const currentUser = appConfig?.currentUser;
  const voicePortalPhoneNumber = appConfig?.voicePortalPhoneNumber;
  const { doPubNubSubscribe, doPubNubUnsubscribe } = usePubNubSubscriptions();

  const onDrawerClose = () => {
    // this removes the listingId from the URL and
    // closes the drawer, being sure to preserve
    // any other search params that were already there!
    searchParams.delete('listing_id');

    navigate(
      `${window.location.pathname}?${searchParams.toString()}`,
      { replace: true },
    );

    setLocalListing(null);
  };

  const onFeedbackClick = () => {
    searchParams.set('listing_id', listingId);
    searchParams.set('feedback_listing_id', listingId);

    navigate(
      `${window.location.pathname}?${searchParams.toString()}`,
      { replace: true },
    );
  };

  const onReportListingClick = () => {
    // this closes the drawer because it doesn't play very nicely with our
    // custom modal system. Using a MUI Portal partially solves the
    // z-index issues I encountered, but even then I couldn't get the
    // textarea to gain focus. This is the best workaround (possibly for now)
    searchParams.delete('listing_id');

    navigate(
      `${window.location.pathname}?${searchParams.toString()}`,
      { replace: true },
    );

    openModal({
      component: ReportContentModal,
      props: {
        contentId: listingId,
        contentType: 'listing',
      },
    });
  };

  const onImageClick = () => {
    setLightboxIsOpen(true);
  };

  const onLightboxClose = () => {
    setLightboxIsOpen(false);
  };

  // because all this does is invalidate the cache tag for the listing,
  // it might be smart to move it to the pubnubcontainer component
  const handlePubNubMessage = useCallback((_msg) => {
    if (!listingId) return;
    dispatchInvalidateCacheTagsEvent([{ type: LISTING_TAG, id: listingId }]);
  }, [listingId]);

  const myCallButton = (listing) => {
    return (
      <Box
        sx={{
          position: 'fixed',
          bottom: '20px',
          right: 'calc(50% - 70px)',
        }}
      >
        {callButton({ listing, currentUser })}
      </Box>
    );
  };

  const updateDocumentTitle = (listing) => {
    if (listing) {
      // remember the previous document title
      setPrevDocTitle(window.document.title);
      // set the new doc title by appending the listing title
      window.document.title = `NiteFlirt Phone Sex | ${listing.title}`;
    } else if (prevDocTitle) {
      window.document.title = prevDocTitle;
    }
  };

  useEffect(() => {
    setLocalListing(listing);
  }, [listing]);

  useEffect(() => {
    updateDocumentTitle(localListing);
  }, [localListing]);

  // listen for changes to the listingId in the URL
  useEffect(() => {
    if (listingId) {
      if (listingId !== listing?.id) {
        triggerGetListingQuery({ id: listingId });
      } else {
        // if we request the same listing 2x, the one we
        // already have cached gets set to localListing
        setLocalListing(listing);
      }
    } else {
      // no listingId in the URL, so close the drawer
      setLocalListing(null);
    }
  }, [listingId]);

  // pubunb stuff
  useEffect(() => {
    if (!listing) return false;

    doPubNubSubscribe({
      pubNub: pubNubClient,
      channels: [listing.push_channel],
      callback: handlePubNubMessage,
    });

    return () => {
      if (!pubNubClient || !listing) return;

      doPubNubUnsubscribe({
        pubNub: pubNubClient,
        channels: [listing.push_channel],
        callback: handlePubNubMessage,
      });
    };
  }, [listing]);

  return (
    <Box>
      <ListingDetailDrawer
        open={!!listingId && !!localListing}
        onClose={onDrawerClose}
        listing={localListing}
        onFeedbackClick={onFeedbackClick}
        onReportListingClick={onReportListingClick}
        currentUser={currentUser}
        onImageClick={onImageClick}
        voicePortalPhoneNumber={voicePortalPhoneNumber}
        isOwnedByViewer={listing?.is_owned_by_viewer}
      />
      {lightboxIsOpen && (
        <Box>
          <Lightbox
            reactModalStyle={{ overlay: { zIndex: 9000 } }}
            mainSrc={listing.photo_url}
            onCloseRequest={onLightboxClose}
            imageTitle={listing.title}
            toolbarButtons={[myCallButton(listing)]}
          />
        </Box>
      )}
    </Box>
  );
};

export default ListingDetailDrawerContainer;
